// SCR-STAFF-405 — بیعانه‌ها (US-BIL-060، 062، 063): فهرست با وضعیت و مهلت، لینک پرداخت (نشانی فقط هنگام صدور)، پرداخت حضوری در صندوق
// (bil.payment.receive، شیفت باز) و بخشش ضبط با دلیل (bil.refund.approve؛ C-AGENT-566).
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { unwrap, type BilDepositStatus, type BilDepositView, type BilPaymentMethod } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, FormDialog, JalaliDate, ListPage, ReasonDialog, type DataTableColumn } from '@phoenix/ui';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Amount, bilKeys, DepositStatusChip, needsReference, SCR_STAFF_405 } from './common';

const STATUSES: readonly BilDepositStatus[] = ['Pending', 'Paid', 'Applied', 'Forfeited', 'Refundable', 'Refunded', 'Expired'];
const COUNTER: readonly BilPaymentMethod[] = ['CardPos', 'Cash', 'BankTransfer'];

export function DepositsPage() {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const [status, setStatus] = useState<BilDepositStatus | ''>('');
  const [paying, setPaying] = useState<BilDepositView | null>(null);
  const [method, setMethod] = useState<BilPaymentMethod>('CardPos');
  const [reference, setReference] = useState('');
  const [waiving, setWaiving] = useState<BilDepositView | null>(null);
  const deposits = useInfiniteQuery({
    queryKey: bilKeys.deposits(session?.tenantId, status),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => unwrap(tenantApi.GET('/api/v1/billing/deposits', { params: { query: { status: status || undefined, cursor: pageParam, limit: 50 } } })),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: bilKeys.all(session?.tenantId) });
  const pay = useMutation({
    mutationFn: (deposit: BilDepositView) =>
      unwrap(
        tenantApi.POST('/api/v1/billing/deposits/{id}:pay', {
          params: { path: { id: deposit.id } },
          body: { payment: { method, amount: deposit.amount, reference: reference.trim() || null, cardLast4: null } },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setPaying(null);
      setReference('');
      refresh();
    },
  });
  const waive = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      unwrap(tenantApi.POST('/api/v1/billing/deposits/{id}:waive', { params: { path: { id } }, body: { reason }, headers: { 'Idempotency-Key': key.current() } })),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setWaiving(null);
      refresh();
    },
  });
  const full = access.accessMode === 'Full';
  const columns: DataTableColumn<BilDepositView>[] = [
    { id: 'requested', headerKey: 'bil:deposits.columns.requested', cell: (row) => <JalaliDate value={row.requestedAt} withTime /> },
    { id: 'patient', headerKey: 'bil:deposits.columns.patient', cell: (row) => row.patientName ?? '—' },
    { id: 'amount', headerKey: 'bil:deposits.columns.amount', cell: (row) => <Amount money={row.amount} /> },
    { id: 'due', headerKey: 'bil:deposits.columns.due', cell: (row) => <JalaliDate value={row.dueAt} withTime /> },
    { id: 'status', headerKey: 'bil:deposits.columns.status', cell: (row) => <DepositStatusChip status={row.status} /> },
    {
      id: 'actions',
      headerKey: 'bil:deposits.columns.actions',
      cell: (row) => (
        <Stack direction="row" spacing={1}>
          {row.status === 'Pending' && full && access.can('bil.payment.receive') ? (
            <Button size="small" onClick={() => setPaying(row)} data-testid="bil-deposit-pay">
              {t('bil:deposits.pay')}
            </Button>
          ) : null}
          {row.status === 'Forfeited' && full && access.can('bil.refund.approve') ? (
            <Button size="small" onClick={() => setWaiving(row)} data-testid="bil-deposit-waive">
              {t('bil:deposits.waive')}
            </Button>
          ) : null}
        </Stack>
      ),
    },
  ];
  return (
    <ListPage
      screenId={SCR_STAFF_405}
      titleKey="bil:deposits.title"
      subtitleKey="bil:deposits.subtitle"
      filters={
        <TextField select size="small" label={t('bil:deposits.columns.status')} value={status} onChange={(event) => setStatus(event.target.value as BilDepositStatus | '')} sx={{ minWidth: 200 }}>
          <MenuItem value="">{t('bil:invoices.all')}</MenuItem>
          {STATUSES.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`bil:deposit_status.${value}`)}
            </MenuItem>
          ))}
        </TextField>
      }
    >
      <DataTable
        captionKey="bil:deposits.title"
        columns={columns}
        rows={deposits.data?.pages.flatMap((page) => page.items)}
        getRowId={(row) => row.id}
        isLoading={deposits.isPending}
        error={deposits.error}
        onRetry={() => void deposits.refetch()}
        hasMore={deposits.hasNextPage}
        onLoadMore={() => void deposits.fetchNextPage()}
        isFetchingMore={deposits.isFetchingNextPage}
        emptyTitleKey="bil:deposits.empty"
      />
      <FormDialog
        open={paying !== null}
        titleKey="bil:deposits.pay"
        onSubmit={() => paying && pay.mutate(paying)}
        onClose={() => setPaying(null)}
        isDirty={reference !== ''}
        isSubmitting={pay.isPending}
        submitError={pay.error}
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          {paying ? <Amount money={paying.amount} testId="bil-deposit-pay-amount" /> : null}
          <TextField select label={t('bil:refunds.columns.method')} value={method} onChange={(event) => setMethod(event.target.value as BilPaymentMethod)} slotProps={{ htmlInput: { 'data-testid': 'bil-deposit-method' } }}>
            {COUNTER.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`bil:method.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          {needsReference(method) ? (
            <TextField required label={t('bil:cashier.pos_reference')} value={reference} onChange={(event) => setReference(event.target.value)} slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'bil-deposit-reference' } }} />
          ) : null}
        </Stack>
      </FormDialog>
      <ReasonDialog
        open={waiving !== null}
        titleKey="bil:deposits.waive"
        busy={waive.isPending}
        error={waive.error}
        onConfirm={(reason) => waiving && waive.mutate({ id: waiving.id, reason })}
        onCancel={() => setWaiving(null)}
      />
      {waive.error && !waiving ? <ErrorState error={waive.error} compact /> : null}
    </ListPage>
  );
}
